import React, { useState, useEffect, useRef } from 'react';
import './InfoUser.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUser,
    faCog,
    faUsers,
    faSuitcaseRolling,
    faGlobe,
    faSignOutAlt,
    faChevronDown,
    faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import baseURL from '../../url';

export default function InfoUser() {
    const [usuario, setUsuario] = useState({});
    const [loading, setLoading] = useState(true);
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        let isMounted = true;
        fetch(`${baseURL}/userLogued.php`, { credentials: 'include' })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.json();
            })
            .then(data => {
                if (isMounted) {
                    setUsuario(data || {});
                    setLoading(false);
                }
            })
            .catch(error => {
                console.error('Error al obtener datos de usuario:', error);
                if (isMounted) setLoading(false);
            });

        return () => { isMounted = false; };
    }, []);

    // Cerrar menú con click afuera y con tecla Escape
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                setMenuOpen(false);
            }
        };

        if (menuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [menuOpen]);

    // Generar iniciales del usuario
    const getInitials = (name) => {
        if (!name || typeof name !== 'string') return 'AD';
        const parts = name.trim().split(/\s+/);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return name.slice(0, 2).toUpperCase();
    };

    const handleLogout = () => {
        setMenuOpen(false);
        Swal.fire({
            title: '¿Cerrar sesión?',
            text: 'Saldrás del panel de administración.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Sí, cerrar sesión',
            cancelButtonText: 'Cancelar',
            reverseButtons: true
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const res = await fetch(`${baseURL}/logout.php`, {
                        method: 'GET',
                        credentials: 'include'
                    });
                    if (res.ok) {
                        Swal.fire({
                            icon: 'success',
                            title: 'Sesión finalizada',
                            text: 'Hasta pronto',
                            timer: 1200,
                            showConfirmButton: false
                        });
                        setTimeout(() => {
                            window.location.href = '/';
                        }, 1000);
                    } else {
                        window.location.reload();
                    }
                } catch (e) {
                    window.location.reload();
                }
            }
        });
    };

    const userName = usuario.nombre || 'Administrador';
    const userRole = usuario.rol ? (usuario.rol === 'admin' ? 'Admin' : usuario.rol) : 'Admin';
    const isAdmin = usuario.rol === 'admin' || !usuario.rol;
    const initials = getInitials(userName);

    return (
        <div className="dashProfileWrapper" ref={menuRef}>
            <button
                type="button"
                className={`dashProfileBtn ${menuOpen ? 'active' : ''}`}
                onClick={() => setMenuOpen(!menuOpen)}
                aria-expanded={menuOpen}
                aria-haspopup="true"
                title="Opciones de perfil y cuenta"
            >
                <div className="dashProfileAvatar">
                    <span className="dashProfileInitials">{initials}</span>
                    <span className="dashOnlineDot" title="En línea"></span>
                </div>
                <div className="dashProfileInfo">
                    <span className="dashProfileName">
                        {loading ? 'Cargando...' : userName}
                    </span>
                    <span className="dashProfileRoleBadge">{userRole}</span>
                </div>
                <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`dashProfileCaret ${menuOpen ? 'open' : ''}`}
                />
            </button>

            {menuOpen && (
                <div className="dashProfileDropdown">
                    <div className="dashProfileHeader">
                        <div className="dashProfileHeaderAvatar">
                            {initials}
                        </div>
                        <div className="dashProfileHeaderDetails">
                            <span className="dashProfileHeaderName">{userName}</span>
                            <span className="dashProfileHeaderEmail">
                                {usuario.email || 'admin@sistema.com'}
                            </span>
                            <div className="dashProfileHeaderStatus">
                                <span className="dashStatusIndicator"></span>
                                <span>En línea • {isAdmin ? 'Administrador' : 'Colaborador'}</span>
                            </div>
                        </div>
                    </div>

                    <div className="dashProfileMenuDivider"></div>

                    <div className="dashProfileMenuItems">
                        <Link
                            to="/dashboard/configuracion"
                            onClick={() => setMenuOpen(false)}
                            className="dashProfileMenuItem"
                        >
                            <div className="dashMenuIconWrap cog">
                                <FontAwesomeIcon icon={faCog} />
                            </div>
                            <div className="dashMenuText">
                                <span className="dashMenuTitle">Configuración & SEO</span>
                                <span className="dashMenuDesc">Marca, redes, PWA y sistema</span>
                            </div>
                        </Link>

                        {isAdmin && (
                            <Link
                                to="/dashboard/usuarios"
                                onClick={() => setMenuOpen(false)}
                                className="dashProfileMenuItem"
                            >
                                <div className="dashMenuIconWrap users">
                                    <FontAwesomeIcon icon={faUsers} />
                                </div>
                                <div className="dashMenuText">
                                    <span className="dashMenuTitle">Usuarios del Sistema</span>
                                    <span className="dashMenuDesc">Gestionar accesos y roles</span>
                                </div>
                            </Link>
                        )}

                        <Link
                            to="/dashboard/productos"
                            onClick={() => setMenuOpen(false)}
                            className="dashProfileMenuItem"
                        >
                            <div className="dashMenuIconWrap suitcase">
                                <FontAwesomeIcon icon={faSuitcaseRolling} />
                            </div>
                            <div className="dashMenuText">
                                <span className="dashMenuTitle">Tours y Paquetes</span>
                                <span className="dashMenuDesc">Catálogo de destinos y viajes</span>
                            </div>
                        </Link>

                        <Link
                            to="/"
                            onClick={() => setMenuOpen(false)}
                            className="dashProfileMenuItem"
                        >
                            <div className="dashMenuIconWrap globe">
                                <FontAwesomeIcon icon={faGlobe} />
                            </div>
                            <div className="dashMenuText">
                                <span className="dashMenuTitle">Ver Portada Pública</span>
                                <span className="dashMenuDesc">Ir al sitio web oficial</span>
                            </div>
                        </Link>
                    </div>

                    <div className="dashProfileMenuDivider"></div>

                    <div className="dashProfileFooter">
                        <button
                            type="button"
                            onClick={handleLogout}
                            className="dashProfileLogoutBtn"
                        >
                            <FontAwesomeIcon icon={faSignOutAlt} />
                            <span>Cerrar Sesión</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
